"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";

const EVENT_DESCRIPTION_TRANSLATION_LANGS = new Set(["en", "ru", "sk"]);

function normalizeEventDescriptionTranslationLang(raw) {
  const value = String(raw || "").trim().toLowerCase().split("-")[0];
  return EVENT_DESCRIPTION_TRANSLATION_LANGS.has(value) ? value : "en";
}

function showTranslationError(message) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("global-error", {
      detail: {
        message:
          message ||
          "Event description translation is temporarily unavailable. Please try again.",
        status: 503,
      },
    })
  );
}

const TranslatableEventDescription = ({
  eventId,
  title,
  description,
  titleClassName = "",
  wrapperClassName = "",
  descriptionClassName = "",
  translateButtonClassName = "",
  translateButtonPlacement = "header",
  translateIconSrc = "",
  translateIconClassName = "mr-1 h-4 w-4",
}) => {
  const { i18n } = useTranslation("events");
  const originalText = String(description || "");
  const targetLang = normalizeEventDescriptionTranslationLang(
    i18n.resolvedLanguage || i18n.language
  );

  const [translationLoading, setTranslationLoading] = React.useState(false);
  const [showTranslated, setShowTranslated] = React.useState(false);
  const [translationMap, setTranslationMap] = React.useState({});

  React.useEffect(() => {
    setTranslationLoading(false);
    setShowTranslated(false);
    setTranslationMap({});
  }, [eventId, originalText]);

  const translatedText = translationMap[targetLang] || "";
  const displayText = showTranslated && translatedText ? translatedText : originalText;
  const canToggleTranslation = Boolean(
    targetLang !== "ru" && eventId && originalText.trim()
  );

  const toggleTranslation = async () => {
    if (!canToggleTranslation || translationLoading) return;

    if (showTranslated) {
      setShowTranslated(false);
      return;
    }

    if (translatedText) {
      setShowTranslated(true);
      return;
    }

    try {
      setTranslationLoading(true);
      const resp = await api.post(`/events/${eventId}/translate-description/`, {
        target_lang: targetLang,
      });
      const nextText = String(resp?.data?.text || "").trim();
      if (nextText) {
        setTranslationMap((prev) => ({ ...prev, [targetLang]: nextText }));
        setShowTranslated(true);
      }
    } catch (error) {
      const backendMessage = String(error?.response?.data?.error || "").trim();
      showTranslationError(backendMessage);
    } finally {
      setTranslationLoading(false);
    }
  };

  const translateButtonLabel = showTranslated
    ? "Original"
    : translationLoading
      ? "Translating..."
      : "Translate";
  const translateButton = canToggleTranslation ? (
    <button
      type="button"
      onClick={toggleTranslation}
      disabled={translationLoading}
      className={`${translateButtonClassName || "shrink-0 bg-transparent p-0 text-right text-base font-semibold text-white underline underline-offset-2"} ${
        translationLoading ? "cursor-wait opacity-80" : ""
      }`}
    >
      {translateIconSrc ? (
        <img
          src={translateIconSrc}
          alt=""
          aria-hidden="true"
          className={translateIconClassName}
        />
      ) : null}
      <span>{translateButtonLabel}</span>
    </button>
  ) : null;

  return (
    <div className={wrapperClassName}>
      <div className="mb-1 flex items-start justify-between gap-3">
        <h2 className={titleClassName}>{title}</h2>
        {translateButtonPlacement === "header" ? translateButton : null}
      </div>
      <p className={descriptionClassName}>
        {displayText}
        {translateButtonPlacement === "inline-after-description" ? translateButton : null}
      </p>
      {translateButtonPlacement === "after-description" && translateButton ? (
        <div className="mt-[8px] flex justify-start">
          {translateButton}
        </div>
      ) : null}
    </div>
  );
};

export default TranslatableEventDescription;
